<template>
  <div :id="component_id" class="w-full flex flex-col justify-start items-start gap-8">
    <div class="self-stretch flex flex-col justify-start items-start gap-8">
      <div class="self-stretch flex flex-col justify-start items-start gap-2">
        <div class="w-60 justify-start text-text-default/80 text-base font-semibold leading-5">{{ $t('settings.general.basicPreferences.title') }}</div>
        <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight">{{ $t('settings.general.basicPreferences.description') }}</div>
      </div>
      <div class="self-stretch flex flex-col justify-start items-start gap-6">
        <div class="self-stretch inline-flex justify-start items-center gap-4">
          <img data-size="Small" data-type="Photo" class="w-16 h-16 rounded-[100px] outline outline-[5px] outline-brand-outline" src="https://placehold.co/64x64" />
          <div class="flex-1 pb-2 inline-flex flex-col justify-start items-start gap-2.5">
            <div class="justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.general.profilePhoto.label') }}</div>
            <div class="self-stretch inline-flex flex-wrap justify-start items-start gap-x-3 gap-y-2">
              <div class="self-stretch bg-btn-primary rounded inline-flex flex-col justify-center items-center gap-2 overflow-hidden">
                <div class="pl-3 pr-4 py-2 inline-flex justify-center items-center gap-2 text-text-inverse">
                  <i class="ri-upload-2-line mr-2" />
                  <div class="text-center justify-center text-sm font-medium leading-5 tracking-tight whitespace-nowrap">{{ $t('settings.general.profilePhoto.upload') }}</div>
                </div>
              </div>
              <div data-leading-icon="False" data-state="Enabled" data-trailing-icon="False" data-type="Outline" class="h-10 bg-transparent rounded outline outline-1 outline-offset-[-1px] outline-brand-outline inline-flex flex-col justify-center items-center gap-2 overflow-hidden">
                <div class="px-4 py-2 inline-flex justify-center items-center gap-2">
                  <div class="text-center justify-center text-text-link text-sm font-medium leading-4 tracking-tight">{{ $t('common.remove') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="self-stretch flex flex-col justify-start items-start">
          <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
            <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.general.dealershipRole.label') }}</div>
          </div>
          <fusion-dropdown :key="`${component_id}_dealership_role`" v-model="dealership_role" :options="role_options" :placeholder="$t('settings.general.dealershipRole.placeholder')" />
        </div>
        <div v-if="false" class="self-stretch flex flex-col justify-start items-start">
          <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
            <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.general.preferredDealership.label') }}</div>
          </div>
          <fusion-dropdown :key="`${component_id}_preferred_dealership`" v-model="preferred_dealership" :options="dealership_options" :placeholder="$t('settings.general.preferredDealership.placeholder')" :disabled="true" />
        </div>
      </div>
    </div>
    <div class="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-separator"></div>
    <div class="self-stretch flex flex-col justify-start items-start gap-8">
      <div class="self-stretch flex flex-col justify-start items-start gap-2">
        <div class="w-60 justify-start text-text-default/80 text-base font-semibold leading-5">{{ $t('settings.general.workingHours.title') }}</div>
        <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight">{{ $t('settings.general.workingHours.description') }}</div>
      </div>
      <div class="self-stretch flex flex-col justify-start items-start gap-4">
        <div class="self-stretch flex flex-col justify-start items-start">
          <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
            <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.general.timezone.label') }}</div>
          </div>
          <fusion-dropdown :key="`${component_id}_timezone`" v-model="timezone" :options="timezone_options" :placeholder="$t('settings.general.timezone.placeholder')" />
        </div>
        <div class="justify-start text-text-default text-sm leading-4 tracking-tight">{{ $t('settings.general.workingHours.scheduledDays') }}</div>
        <div class="self-stretch h-8 px-2 bg-surface-2 rounded inline-flex justify-between items-center gap-2">
          <div class="w-28 justify-start text-text-subtle text-sm font-semibold leading-4 tracking-tight">{{ $t('settings.general.workingHours.columnDay') }}</div>
          <div class="flex-1 text-center text-text-subtle text-sm font-semibold leading-4 tracking-tight">{{ $t('settings.general.workingHours.columnStart') }}</div>
          <div class="flex-1 text-center text-text-subtle text-sm font-semibold leading-4 tracking-tight">{{ $t('settings.general.workingHours.columnEnd') }}</div>
        </div>
        <div class="self-stretch flex flex-col justify-start items-start gap-6">
          <div class="self-stretch flex flex-col justify-start items-start">
            <!-- Day rows -->
            <div v-for="(day, index) in schedule_days" :key="day.key" class="self-stretch h-14 px-2 py-2 bg-surface-1 inline-flex justify-start items-center gap-2" :class="{ 'border-b border-border-separator': index < schedule_days.length - 1 }">
              <div class="w-28 flex justify-between items-center">
                <div class="text-text-default text-xs leading-4 tracking-tight">{{ $t('common.days.' + day.key) }}</div>
                <div class="w-8 h-7 inline-flex flex-col justify-center items-start">
                  <fusion-switch :key="`${component_id}_${day.key}_switch`" v-model="day.enabled" />
                </div>
              </div>
              <div class="flex-1">
                <fusion-dropdown :key="`${component_id}_${day.key}_start`" v-model="day.start" :options="time_options" :disabled="!day.enabled" :placeholder="$t('settings.general.workingHours.columnStart')" sort-order="none" />
              </div>
              <div class="flex-1">
                <fusion-dropdown :key="`${component_id}_${day.key}_end`" v-model="day.end" :options="time_options" :disabled="!day.enabled" :placeholder="$t('settings.general.workingHours.columnEnd')" sort-order="none" />
              </div>
            </div>
          </div>
        </div>
        <div class="justify-start text-text-link text-xs underline leading-4 tracking-tight cursor-pointer">{{ $t('settings.general.workingHours.outOfOffice') }}</div>
      </div>
    </div>
    <div class="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-separator"></div>
    <div class="self-stretch flex flex-col justify-start items-start gap-8">
      <div class="flex flex-col justify-start items-start gap-2">
        <div class="w-60 justify-start text-text-default/80 text-base font-semibold leading-5">{{ $t('settings.general.interfacePreferences.title') }}</div>
        <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight">{{ $t('settings.general.interfacePreferences.description') }}</div>
      </div>
      <div class="self-stretch flex flex-col justify-start items-start gap-8">
        <div class="self-stretch flex flex-col justify-start items-start">
          <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
            <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.general.theme.label') }}</div>
          </div>
          <fusion-dropdown :key="`${component_id}_theme`" v-model="theme" :options="theme_options" :placeholder="$t('settings.general.theme.placeholder')" />
        </div>
      </div>
      <div class="self-stretch flex flex-col justify-start items-start gap-2.5">
        <div class="self-stretch flex flex-col justify-start items-start gap-8">
          <div class="self-stretch flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.general.systemLanguage.label') }}</div>
            </div>
            <fusion-dropdown :key="`${component_id}_system_language`" v-model="system_language" :options="language_options" :placeholder="$t('common.selectLanguage')" />
            <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight mt-1">{{ $t('settings.general.systemLanguage.description') }}</div>
            <div class="self-stretch inline-flex justify-start items-center gap-2 mt-2">
              <fusion-checkbox v-model="use_system_language_as_spoken" />
              <div class="justify-start text-text-default text-xs leading-4 tracking-tight cursor-pointer" @click="use_system_language_as_spoken = !use_system_language_as_spoken">{{ $t('settings.general.systemLanguage.useAsSpoken') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import FusionDropdown from '@components/UI/FusionDropdown.vue';
import FusionSwitch from '@components/UI/FusionSwitch.vue';
import FusionCheckbox from '@components/UI/FusionCheckbox.vue';
import { TIMEZONE_OPTIONS } from '../../utils/timezones';
import { getEnabledLocales } from '../../i18n';

export default {
  components: {
    'fusion-dropdown': FusionDropdown,
    'fusion-switch': FusionSwitch,
    'fusion-checkbox': FusionCheckbox,
  },
  props: {
    settings: {
      type: Object,
      required: false,
      default: null
    },
  },
  data() {
    return {
      // Wired fields (matched to backend)
      timezone: 'America/Chicago',
      theme: 'system',
      system_language: 'en-US',
      use_system_language_as_spoken: false,
      dealership_role: null,
      
      // Not wired yet (no backend field or coming from elsewhere)
      preferred_dealership: null,
      
      // Options arrays - using backend format values
      // Localized option lists (dealership/role/timezone/theme) are computed, not data, so their
      // labels re-render when the UI language changes.
      // (language_options is computed too — see below; it filters by the instance's enabled locales.)
      //
      time_options: [
        { label: '06:00 AM', value: '06:00' },
        { label: '06:30 AM', value: '06:30' },
        { label: '07:00 AM', value: '07:00' },
        { label: '07:30 AM', value: '07:30' },
        { label: '08:00 AM', value: '08:00' },
        { label: '08:30 AM', value: '08:30' },
        { label: '09:00 AM', value: '09:00' },
        { label: '09:30 AM', value: '09:30' },
        { label: '10:00 AM', value: '10:00' },
        { label: '10:30 AM', value: '10:30' },
        { label: '11:00 AM', value: '11:00' },
        { label: '11:30 AM', value: '11:30' },
        { label: '12:00 PM', value: '12:00' },
        { label: '12:30 PM', value: '12:30' },
        { label: '01:00 PM', value: '13:00' },
        { label: '01:30 PM', value: '13:30' },
        { label: '02:00 PM', value: '14:00' },
        { label: '02:30 PM', value: '14:30' },
        { label: '03:00 PM', value: '15:00' },
        { label: '03:30 PM', value: '15:30' },
        { label: '04:00 PM', value: '16:00' },
        { label: '04:30 PM', value: '16:30' },
        { label: '05:00 PM', value: '17:00' },
        { label: '05:30 PM', value: '17:30' },
        { label: '06:00 PM', value: '18:00' },
        { label: '06:30 PM', value: '18:30' },
        { label: '07:00 PM', value: '19:00' },
        { label: '07:30 PM', value: '19:30' },
        { label: '08:00 PM', value: '20:00' },
        { label: '08:30 PM', value: '20:30' },
        { label: '09:00 PM', value: '21:00' },
        { label: '09:30 PM', value: '21:30' },
        { label: '10:00 PM', value: '22:00' },
      ],
      // Day names are not stored here — the template resolves them from `key` via
      // $t('common.days.<key>') so they follow the UI language.
      schedule_days: [
        { key: 'sun', enabled: false, start: '08:00', end: '18:00' },
        { key: 'mon', enabled: true, start: '08:00', end: '18:00' },
        { key: 'tue', enabled: true, start: '08:00', end: '18:00' },
        { key: 'wed', enabled: true, start: '08:00', end: '18:00' },
        { key: 'thu', enabled: true, start: '08:00', end: '18:00' },
        { key: 'fri', enabled: true, start: '08:00', end: '18:00' },
        { key: 'sat', enabled: false, start: '08:00', end: '18:00' },
      ],
      
      // Track original values for change detection
      original_values: {},
    }
  },
  created() {
    this.loadFromSettings();
  },
  computed: {
    component_key() {
      return this.$attrs.key ? this.$attrs.key : ('general_settings_' + Date.now());
    },
    component_id() {
      return this.component_key + '_container';
    },
    // Localized dropdown options. Values stay in the backend's format — only labels are localized.
    dealership_options() {
      return [
        { label: this.$t('settings.general.dealerships.auto'), value: 'auto' },
      ];
    },
    role_options() {
      return [
        { label: this.$t('settings.general.roles.salesperson'), value: 'salesperson' },
        { label: this.$t('settings.general.roles.sales_manager'), value: 'sales_manager' },
        { label: this.$t('settings.general.roles.finance_manager'), value: 'finance_manager' },
        { label: this.$t('settings.general.roles.general_manager'), value: 'general_manager' },
        { label: this.$t('settings.general.roles.service_advisor'), value: 'service_advisor' },
        { label: this.$t('settings.general.roles.service_technician'), value: 'service_technician' },
        { label: this.$t('settings.general.roles.parts_manager'), value: 'parts_manager' },
        { label: this.$t('settings.general.roles.bdc_rep'), value: 'bdc_rep' },
      ];
    },
    timezone_options() {
      // The list itself now lives in utils/timezones so the scheduled-task editor offers exactly the same
      // zones — the values are persisted and resolved server-side, so two copies drifting apart would be a
      // data problem, not a cosmetic one. Same output shape as before: { label, value }.
      return TIMEZONE_OPTIONS.map(o => ({ label: this.$t(o.labelKey), value: o.value }));
    },
    theme_options() {
      return [
        { label: this.$t('settings.general.themes.light'), value: 'light' },
        { label: this.$t('settings.general.themes.dark'), value: 'dark' },
        { label: this.$t('settings.general.themes.system'), value: 'system' },
      ];
    },
    // Language names are deliberately NOT translated: each is shown in its own language
    // (endonym) so a user can find their language whatever the current interface language is.
    // The full list is filtered by the instance's enabled locales (server-supported reply
    // languages — German is opt-in per instance), compared by primary tag since the full
    // backend values carry regional subtags (de-DE vs de).
    language_options() {
      const enabled = getEnabledLocales();
      return [
        { label: 'English (US)', value: 'en-US' },
        { label: 'English (UK)', value: 'en-GB' },
        { label: 'Español (México)', value: 'es-MX' },
        { label: 'Français (Canada)', value: 'fr-CA' },
        { label: 'Français (France)', value: 'fr-FR' },
        { label: 'Deutsch', value: 'de-DE' },
      ].filter(option => enabled.includes(option.value.split('-')[0].toLowerCase()));
    },
    // Build patch object with only changed values
    settings_patch() {
      const patch = {};
      
      // Check each wired field for changes
      if (this.timezone !== this.original_values.timezone) {
        patch['Time.Timezone'] = this.timezone;
      }
      if (this.theme !== this.original_values.theme) {
        patch['Ux.Theme'] = this.theme;
      }
      if (this.system_language !== this.original_values.system_language) {
        patch['Ux.Language'] = this.system_language;
        // [NPM-120] Marks the change as user-chosen so a trusted app's declared language
        // can never override it (server-side TrustedAppReplyLanguageResolver reads this marker).
        patch['Ux.LanguageSource'] = 'user';
      }
      if (this.use_system_language_as_spoken !== this.original_values.use_system_language_as_spoken) {
        patch['Ux.UseSystemLanguageAsSpoken'] = this.use_system_language_as_spoken;
      }
      // When checkbox is checked, keep SpokenLanguage in sync with SystemLanguage
      if (this.use_system_language_as_spoken) {
        if (this.system_language !== this.original_values.system_language || !this.original_values.use_system_language_as_spoken) {
          patch['Ux.SpokenLanguage'] = this.system_language;
        }
      }
      if (this.dealership_role !== this.original_values.dealership_role) {
        patch['Profile.DealerRoleCode'] = this.dealership_role;
      }
      
      // Check if working hours changed
      if (this.workingHoursChanged()) {
        patch['Time.WorkingHours'] = this.buildWorkingHoursPayload();
      }
      
      return patch;
    },
    has_changes() {
      return Object.keys(this.settings_patch).length > 0;
    },
  },
  watch: {
    has_changes: {
      handler(newVal) {
        this.$emit('changes', newVal);
      },
      immediate: true
    }
  },
  methods: {
    loadFromSettings() {
      const flat = this.settings?.flat || {};
      
      // Load wired fields from backend
      this.timezone = flat['Time.Timezone'] || 'America/Chicago';
      this.theme = flat['Ux.Theme'] || 'system';
      this.system_language = flat['Ux.Language'] || 'en-US';
      this.use_system_language_as_spoken = flat['Ux.UseSystemLanguageAsSpoken'] === true || flat['Ux.UseSystemLanguageAsSpoken'] === 'true';
      this.dealership_role = flat['Profile.DealerRoleCode'] || null;
      
      // Load working hours from backend
      this.loadWorkingHours(flat['Time.WorkingHours']);
      
      // Store original values for change detection
      this.original_values = {
        timezone: this.timezone,
        theme: this.theme,
        system_language: this.system_language,
        use_system_language_as_spoken: this.use_system_language_as_spoken,
        dealership_role: this.dealership_role,
        schedule_days: JSON.parse(JSON.stringify(this.schedule_days)),
      };
    },
    loadWorkingHours(workingHours) {
      if (!Array.isArray(workingHours) || workingHours.length === 0) return;
      
      // Reset all days to disabled first
      this.schedule_days.forEach(day => {
        day.enabled = false;
        day.start = '08:00';
        day.end = '18:00';
      });
      
      // Map backend day codes to our keys
      const dayCodeMap = {
        'Sun': 'sun', 'Mon': 'mon', 'Tue': 'tue', 'Wed': 'wed',
        'Thu': 'thu', 'Fri': 'fri', 'Sat': 'sat'
      };
      
      // Process each working hours profile
      workingHours.forEach(profile => {
        if (!profile.DaysOfWeek) return;
        
        profile.DaysOfWeek.forEach(dayCode => {
          const dayKey = dayCodeMap[dayCode];
          const day = this.schedule_days.find(d => d.key === dayKey);
          if (day) {
            day.enabled = true;
            day.start = profile.StartLocal || '08:00';
            day.end = profile.EndLocal || '18:00';
          }
        });
      });
    },
    buildWorkingHoursPayload() {
      // Group enabled days by their start/end times
      const groups = {};
      
      this.schedule_days.filter(d => d.enabled).forEach(day => {
        const key = `${day.start}-${day.end}`;
        if (!groups[key]) {
          groups[key] = { start: day.start, end: day.end, days: [] };
        }
        // Map key back to backend format
        const dayCodeMap = {
          'sun': 'Sun', 'mon': 'Mon', 'tue': 'Tue', 'wed': 'Wed',
          'thu': 'Thu', 'fri': 'Fri', 'sat': 'Sat'
        };
        groups[key].days.push(dayCodeMap[day.key]);
      });
      
      // Build profiles array
      return Object.values(groups).map((group, idx) => ({
        Name: idx === 0 ? 'Working Hours' : `Working Hours ${idx + 1}`,
        DaysOfWeek: group.days,
        StartLocal: group.start,
        EndLocal: group.end,
        TimezoneRef: null
      }));
    },
    workingHoursChanged() {
      const original = this.original_values.schedule_days || [];
      return this.schedule_days.some((day, i) => {
        const orig = original[i];
        if (!orig) return true;
        return day.enabled !== orig.enabled || 
               day.start !== orig.start || 
               day.end !== orig.end;
      });
    },
    save() {
      const patch = this.settings_patch;
      if (Object.keys(patch).length > 0)
        this.$emit('save', patch);
      else
        this.$emit('cancel');
    },
    cancel() {
      this.$emit('cancel');
    },
  },
}
</script>
